// Location policies carry the first four; global flags carry mode (+ isEnabled, which
// every write sets to true and so never tells the admin anything).
const FIELD_ORDER = ["mode", "circuityFactor", "speedKmh", "notes"];
const IGNORED = new Set(["isEnabled"]);

export interface FieldChange {
	field: string;
	from: unknown;
	to: unknown;
}

/** The fields whose value moved between two history snapshots. */
export function changedFields(
	oldValue: Record<string, unknown> | null,
	newValue: Record<string, unknown> | null,
): FieldChange[] {
	const before = oldValue ?? {};
	const after = newValue ?? {};
	const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])]
		.filter((k) => !IGNORED.has(k))
		.sort((a, b) => rank(a) - rank(b));

	return keys
		.map((field) => ({
			field,
			from: before[field] ?? null,
			to: after[field] ?? null,
		}))
		.filter((c) => JSON.stringify(c.from) !== JSON.stringify(c.to));
}

function rank(field: string) {
	const i = FIELD_ORDER.indexOf(field);
	return i === -1 ? FIELD_ORDER.length : i;
}

const dayKey = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Jakarta" });

/** Consecutive rows grouped by the WIB calendar day of `at(row)`, order kept. */
export function groupByDay<T>(rows: T[], at: (row: T) => string) {
	const groups: { day: string; rows: T[] }[] = [];
	for (const row of rows) {
		const day = dayKey.format(new Date(at(row)));
		const last = groups.at(-1);
		if (last?.day === day) last.rows.push(row);
		else groups.push({ day, rows: [row] });
	}
	return groups;
}
